import {
  Alert,
  Button,
  CopyButton,
  QR_LEVEL,
  cx,
  formatDuration,
  formatUsd,
  networkLabel,
  qrLogo,
  useRemaining,
} from "@unizpay/ui";
import { ArrowLeft, Clock } from "lucide-react";
import { QRCodeSVG } from "qrcode.react";
import { type ReactNode, useEffect, useRef, useState } from "react";
import type { IssuedAddress } from "../types";
import { CoinIcon } from "./CoinIcon";

/** The address's window to pay: a quiet pill, amber in its last five minutes. */
function ExpiresIn({ left, until }: { left: number; until: string }) {
  const soon = left < 5 * 60_000;
  return (
    <p
      className={cx(
        "inline-flex shrink-0 items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-medium",
        soon ? "bg-warn-subtle text-warn" : "bg-subtle text-fg-muted",
      )}
    >
      <Clock aria-hidden="true" className="size-3.5" />
      <time className="tabular-nums" dateTime={until}>
        {formatDuration(left)}
      </time>
      <span className="sr-only">left to send</span>
    </p>
  );
}

/** One line of what to send: label, value and a copy button for the full value. */
function DetailRow({
  label,
  copy,
  copyLabel,
  children,
}: {
  label: string;
  copy: string;
  copyLabel: string;
  children: ReactNode;
}) {
  return (
    <div className="flex items-center justify-between gap-3 py-2 pr-2 pl-3.5">
      <dt className="shrink-0 text-fg-muted">{label}</dt>
      <dd className="flex min-w-0 items-center gap-1.5">
        <span className="min-w-0 truncate">{children}</span>
        <CopyButton compact value={copy} label={copyLabel} />
      </dd>
    </div>
  );
}

/**
 * Step 2: exactly what to send and where, and nothing else. The QR encodes a wallet payment
 * request (EIP-681) with token, amount and address, so wallets prefill everything.
 */
export function PayStep({
  issued,
  onBack,
  onRefreshQuote,
  refreshing,
}: {
  issued: IssuedAddress;
  /** Null once funds may already be on their way (no switching methods then). */
  onBack: (() => void) | null;
  onRefreshQuote: () => void;
  refreshing: boolean;
}) {
  const quoteLeft = useRemaining(issued.quote_expires_at);
  const network = networkLabel(issued.network);
  // Paid short: ask only for the rest, at the rate locked when the address was issued, so the
  // window no longer runs out.
  const due = issued.due;
  const amount = due?.amount ?? issued.pay_amount;
  const qr = due?.qr ?? issued.qr;
  const quoteExpired = !due && quoteLeft <= 0;
  // Each address has its own window to pay, from when it was issued (the API says how long);
  // a volatile token's amount is a price lock that is re-quoted when it ends.
  const stable = !issued.price_locked;
  const fee = Number(issued.processing_fee) > 0 ? issued.processing_fee : null;

  // A volatile coin's price lock ran out: fetch a fresh quote right away (once per quote), then
  // tell the payer the amount changed.
  const requotedFor = useRef<string | null>(null);
  const [priceUpdated, setPriceUpdated] = useState(false);
  useEffect(() => {
    if (stable || !quoteExpired || refreshing || requotedFor.current === issued.quote_expires_at) return;
    requotedFor.current = issued.quote_expires_at;
    setPriceUpdated(true);
    onRefreshQuote();
  }, [stable, quoteExpired, refreshing, issued.quote_expires_at, onRefreshQuote]);

  return (
    <div className="space-y-5">
      {onBack && (
        <button
          type="button"
          onClick={onBack}
          className="-ml-1 inline-flex items-center gap-1 rounded px-1 text-[0.8125rem] font-medium text-fg-muted hover:text-fg"
        >
          <ArrowLeft aria-hidden="true" className="size-3.5" />
          Change method
        </button>
      )}

      {!due && !quoteExpired && priceUpdated && (
        <Alert tone="info" title="Price updated">
          The price lock ended. Already sent the earlier amount? Don't send again: stay here, we will detect
          it. Otherwise send the new amount below.
        </Alert>
      )}

      <div className="flex items-start justify-between gap-3">
        <div className="flex min-w-0 items-center gap-3">
          <CoinIcon symbol={issued.token} network={issued.network} size={36} />
          <div className="min-w-0">
            <p className="text-xs text-fg-muted">{due ? "Send the rest" : "Send exactly"}</p>
            <p className="text-xl font-semibold tracking-tight tabular-nums">
              {amount} <span className="text-base font-medium text-fg-muted">{issued.token}</span>
            </p>
            <p className="text-xs text-fg-muted">
              on {network}
              {!due && fee && ` · includes ${formatUsd(fee)} fee`}
            </p>
          </div>
        </div>
        {!due && !quoteExpired && <ExpiresIn left={quoteLeft} until={issued.quote_expires_at} />}
      </div>

      <div className={cx("flex justify-center", quoteExpired && "opacity-20")}>
        <div className="rounded-xl border border-line bg-white p-3">
          <QRCodeSVG
            value={qr}
            size={176}
            level={QR_LEVEL}
            imageSettings={qrLogo(176)}
            marginSize={0}
            bgColor="#ffffff"
            fgColor="#000000"
            title={`Payment request: ${amount} ${issued.token} on ${network}`}
          />
        </div>
      </div>

      <dl className="divide-y divide-line rounded-lg border border-line text-[0.8125rem]">
        <DetailRow label="Amount" copy={amount} copyLabel="amount">
          <span className="font-semibold tabular-nums">
            {amount} {issued.token}
          </span>
        </DetailRow>
        <div className="py-2 pr-2 pl-3.5">
          <div className="flex items-center justify-between gap-3">
            <dt className="text-fg-muted">Address</dt>
            <CopyButton compact value={issued.address} label="address" />
          </div>
          {/* Small enough for a 42-character address on one line on a phone; longer ones split evenly. */}
          <dd className="mt-0.5 pr-1.5 font-mono text-[0.6875rem] leading-relaxed tracking-tight break-all text-balance sm:text-xs sm:tracking-normal">
            {issued.address}
          </dd>
        </div>
      </dl>

      {quoteExpired && stable ? (
        <Alert tone="warn" title="Time to pay at this address has ended">
          <p>Get a new address before sending. Already sent? Stay here; we will detect it.</p>
          <div className="mt-3 flex flex-wrap gap-2">
            <Button size="sm" onClick={onRefreshQuote} loading={refreshing}>
              Get a new address
            </Button>
            {onBack && (
              <Button size="sm" variant="secondary" onClick={onBack} disabled={refreshing}>
                Choose again
              </Button>
            )}
          </div>
        </Alert>
      ) : quoteExpired ? (
        <Alert tone="warn" title="Getting a new price">
          <p>The price lock ended. Already sent? Stay here; we will detect it.</p>
          <Button className="mt-3" size="sm" onClick={onRefreshQuote} loading={refreshing}>
            Get a new amount
          </Button>
        </Alert>
      ) : (
        <>
          <p className="text-center text-xs text-fg-muted">
            Send only <span className="font-medium text-fg">{issued.token}</span> on{" "}
            <span className="font-medium text-fg">{network}</span>. Other coins or networks may be lost.
          </p>
          <p className="flex items-center justify-center gap-2 rounded-lg bg-bg py-2.5 text-[0.8125rem] text-fg-muted">
            <span className="relative flex size-2 shrink-0">
              <span className="absolute inline-flex size-full animate-ping rounded-full bg-ok opacity-50" />
              <span className="relative inline-flex size-2 rounded-full bg-ok" />
            </span>
            Waiting for your payment…
          </p>
        </>
      )}
    </div>
  );
}
